import { type FC, type ReactNode, useCallback, useEffect, useMemo } from 'react';
import {
    graphql,
    useFragment,
    usePreloadedQuery,
    type PreloadedQuery,
    useRefetchableFragment,
} from 'react-relay';
import classnames from 'classnames';

import { Link } from 'dibs-elements/exports/Link';
import LazyImage from 'dibs-react-lazy-image';
import {
    VisibilityTracker,
    type VisibilityChangeCallback,
} from 'dibs-visibility-tracker/exports/VisibilityTracker';

import {
    recentlyViewedItemsQuery,
    useRecentlyViewedItemsContext,
    MOBILE,
    TABLET,
} from './RecentlyViewedItemsContext';
import {
    RecentlyViewedItemsWrapper,
    type RecentlyViewedItemsWrapperProps,
} from './RecentlyViewedItemsWrapper';
import { useRecentHistoryList } from './useRecentHistoryList';
import { RecentlyViewedItemsHeart } from './RecentlyViewedItemsHeart';
import { ACTION_LABEL, SRC_SET_SIZES_ITEM_TILE } from './constants';
import { trackItemImpression, trackItemClick, trackArrowClick } from '../recentHistoryTracking';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';
import { AuctionPricePaddle } from 'dibs-auctions/exports/AuctionPricePaddle';
import { ScrollCarousel } from 'dibs-scroll-carousel/exports/ScrollCarousel';

import { type RecentlyViewedItemsCarousel_viewer$key } from './__generated__/RecentlyViewedItemsCarousel_viewer.graphql';
import { type RecentlyViewedItemsCarouselViewerRefetchQuery } from './__generated__/RecentlyViewedItemsCarouselViewerRefetchQuery.graphql';
import { type RecentlyViewedItemsCarousel_user$key } from './__generated__/RecentlyViewedItemsCarousel_user.graphql';
import { type RecentlyViewedItemsContextQuery } from './__generated__/RecentlyViewedItemsContextQuery.graphql';
import { type ItemType } from '../recentHistoryTypes';

import dibsCss from 'dibs-css';
import styles from './RecentlyViewedItemsCarousel.scss';
import { type RecentlyViewedItemsCarousel_items$key } from './__generated__/RecentlyViewedItemsCarousel_items.graphql';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

const viewerFragment = graphql`
    fragment RecentlyViewedItemsCarousel_viewer on Viewer
    @refetchable(queryName: "RecentlyViewedItemsCarouselViewerRefetchQuery")
    @argumentDefinitions(
        loadPortfolioData: { type: "Boolean", defaultValue: false }
        selectedItemIds: { type: "[String]", defaultValue: [] }
        userIds: { type: "[String]", defaultValue: [] }
    ) {
        ...RecentlyViewedItemsHeart_viewer
            @arguments(
                loadPortfolioData: $loadPortfolioData
                selectedItemIds: $selectedItemIds
                userIds: $userIds
            )
    }
`;

const userFragment = graphql`
    fragment RecentlyViewedItemsCarousel_user on User
    @argumentDefinitions(
        excludeItemPks: { type: "[String]", defaultValue: [] }
        count: { type: "Int", defaultValue: 0 }
    ) {
        ...useRecentHistoryList_user @arguments(excludeItemPks: $excludeItemPks, count: $count)
        ...RecentlyViewedItemsHeart_user @arguments(excludeItemPks: $excludeItemPks, count: $count)
    }
`;

const itemsFragment = graphql`
    fragment RecentlyViewedItemsCarousel_items on Item @relay(plural: true) {
        ...useRecentHistoryList_items
        ...RecentlyViewedItemsHeart_items
    }
`;

type OnInitialDisplay = ({ items }: { items: ItemType[] }) => void;

const Visibility: FC<{
    onInitialDisplay?: OnInitialDisplay;
    items: ItemType[];
    children: ReactNode;
}> = ({ onInitialDisplay, items, children }) => {
    const onVisibilityChange: (props: VisibilityChangeCallback) => void = useCallback(
        ({ isVisible, disconnect }) => {
            if (isVisible && onInitialDisplay && items?.length) {
                onInitialDisplay({ items });
                disconnect();
            }
        },
        [onInitialDisplay, items]
    );

    if (onInitialDisplay) {
        return (
            <VisibilityTracker onVisibilityChange={onVisibilityChange}>
                {children}
            </VisibilityTracker>
        );
    }

    return <>{children}</>;
};

export type RecentlyViewedItemsCarouselProps = {
    authModalLoader: $TSFixMe;
    queryReference: PreloadedQuery<RecentlyViewedItemsContextQuery>;
    trackItemClick?: typeof trackItemClick;
    trackItemImpression?: typeof trackItemImpression;
    onInitialDisplay?: OnInitialDisplay;
    currency: string;
    isBot?: boolean;
} & Omit<RecentlyViewedItemsWrapperProps, 'children'>;

export const RecentlyViewedItemsCarousel: FC<RecentlyViewedItemsCarouselProps> = ({
    wrapperClassName,
    authModalLoader,
    queryReference,
    actionLabel = ACTION_LABEL,
    trackViewMoreClick,
    onInitialDisplay,
    currency,
    hasTopSeparator = false,
    hasBottomSeparator = false,
    ...props
}) => {
    const data = usePreloadedQuery<RecentlyViewedItemsContextQuery>(
        recentlyViewedItemsQuery,
        queryReference
    );
    const user = useFragment<RecentlyViewedItemsCarousel_user$key>(
        userFragment,
        data.viewer?.user || null
    );
    const [viewer, refetch] = useRefetchableFragment<
        RecentlyViewedItemsCarouselViewerRefetchQuery,
        RecentlyViewedItemsCarousel_viewer$key
    >(viewerFragment, data.viewer);
    const itemsRef = useFragment<RecentlyViewedItemsCarousel_items$key>(
        itemsFragment,
        data?.viewer?.itemRead ? data.viewer.itemRead.filter(filterNulls) : null
    );

    const { items, hasFetchedItems } = useRecentHistoryList({ user, itemsRef });
    const { userId, deviceType, itemsPerPage } = useRecentlyViewedItemsContext();
    const isMobile = deviceType === MOBILE;
    const isTablet = deviceType === TABLET;
    const itemsIds = useMemo(() => items?.map(item => item.itemId), [items]);

    useEffect(() => {
        /**
         * Refetch favorites data if recent history contains fetched items
         */
        if (hasFetchedItems && itemsIds && userId) {
            refetch({
                loadPortfolioData: true,
                selectedItemIds: itemsIds,
                userIds: userId ? [userId] : [],
            });
        }
    }, [refetch, itemsIds, userId, hasFetchedItems]);

    if (!items?.length) {
        return null;
    }

    const lazyClassName = classnames(
        styles.lazy,
        dibsCss.flex,
        dibsCss.itemsCenter,
        dibsCss.justifyCenter
    );

    return (
        <Visibility onInitialDisplay={onInitialDisplay} items={items}>
            <RecentlyViewedItemsWrapper
                wrapperClassName={wrapperClassName}
                actionLabel={actionLabel}
                trackViewMoreClick={trackViewMoreClick}
                hasTopSeparator={hasTopSeparator}
                hasBottomSeparator={hasBottomSeparator}
            >
                <ScrollCarousel
                    animation="slide"
                    enableScroll
                    itemsToShow={itemsPerPage}
                    dataTn="recently-viewed-items-carousel"
                    {...(isMobile
                        ? {
                              stepSize: 1,
                              gapSize: 'small',
                              scrollPadding: 'small',
                          }
                        : {
                              stepSize: itemsPerPage,
                              isInfinite: true,
                              showArrows: true,
                              onArrowClick: ({ direction }) =>
                                  trackArrowClick({ direction, actionLabel }),
                          })}
                >
                    {items.map((item, index) => (
                        <div
                            key={item.itemId}
                            className={classnames(
                                styles.itemWrapper,
                                dibsCss.flex,
                                dibsCss.itemsCenter,
                                dibsCss.justifyCenter,
                                {
                                    [styles.swiperItem]: isMobile,
                                }
                            )}
                        >
                            <div
                                className={classnames(styles.itemInner, {
                                    [styles.tablet]: isTablet,
                                })}
                                data-tn="recently-viewed-tile"
                            >
                                <div className={styles.imageContainer}>
                                    <Link
                                        className={styles.imageLink}
                                        href={item.itemPdpUrl}
                                        dataTn="recently-viewed-item"
                                        onClick={() => {
                                            (props.trackItemClick || trackItemClick)({
                                                item,
                                                index,
                                                actionLabel,
                                            });
                                        }}
                                    >
                                        <LazyImage
                                            className={lazyClassName}
                                            placeholderClass={lazyClassName}
                                            onContentVisible={() => {
                                                (props.trackItemImpression || trackItemImpression)({
                                                    items: [item],
                                                    index,
                                                    actionLabel,
                                                });
                                            }}
                                        >
                                            <img
                                                src={item.itemImageUrl}
                                                className={classnames(
                                                    dibsCss.wFull,
                                                    dibsCss.hFull,
                                                    dibsCss.objectContain
                                                )}
                                                alt={item.itemTitle}
                                                sizes={SRC_SET_SIZES_ITEM_TILE}
                                                srcSet={getSrcsetString(item.itemImageUrl)}
                                            />
                                        </LazyImage>
                                    </Link>
                                    <AuctionPricePaddle item={item} showPill currency={currency} />
                                </div>
                                <div className={styles.heartWrapper}>
                                    <RecentlyViewedItemsHeart
                                        currentItem={item}
                                        viewer={viewer}
                                        user={user}
                                        items={itemsRef}
                                        authModalLoader={authModalLoader}
                                        className={styles.heart}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </ScrollCarousel>
            </RecentlyViewedItemsWrapper>
        </Visibility>
    );
};
